import axios from "axios";
import API_URL from "@/config/api";
import router from "@/router";

/*
 * Renouvellement automatique du token.
 *
 * - Quand une requête reçoit un 401, on échange le refresh token contre un nouveau
 *   token, puis on rejoue la requête. L'utilisateur ne voit rien.
 * - Si plusieurs requêtes échouent en même temps, un seul refresh est lancé
 *   et toutes les requêtes attendent son résultat.
 * - Si le refresh échoue (session expirée, compte bloqué…), on déconnecte et on
 *   redirige vers la page de connexion.
 */

// ---------- À adapter si besoin ----------
const SESSIONS = {
  user: {
    match: (url) => !url.includes("/api/admin"),
    refreshUrl: `${API_URL}/api/auth/refresh`,
    logoutUrl: `${API_URL}/api/auth/logout`,
    loginRoute: "/login",
    // localStorage.user = { token, refreshToken, id, name, email, role, ... }
    get() {
      try {
        return JSON.parse(localStorage.getItem("user")) || {};
      } catch {
        return {};
      }
    },
    save(data) {
      localStorage.setItem("user", JSON.stringify({ ...this.get(), ...data }));
    },
    clear() {
      localStorage.removeItem("user");
    },
  },
  admin: {
    match: (url) => url.includes("/api/admin"),
    refreshUrl: `${API_URL}/api/admin/refresh`,
    logoutUrl: `${API_URL}/api/admin/logout`,
    loginRoute: "/admin/login",
    get() {
      return {
        token: localStorage.getItem("adminToken"),
        refreshToken: localStorage.getItem("adminRefreshToken"),
      };
    },
    save(data) {
      localStorage.setItem("adminToken", data.token);
      localStorage.setItem("adminRefreshToken", data.refreshToken);
    },
    clear() {
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminRefreshToken");
    },
  },
};
// -----------------------------------------

// Instance sans intercepteurs, pour appeler /refresh sans boucle infinie
const plain = axios.create();

const AUTH_URLS =
  /\/api\/(auth\/(login|register|refresh|logout)|admin\/(login|refresh|logout))/;

// Tes pages admin appellent /api/admin/... avec le token de l'utilisateur connecté (localStorage.user).
// La session "admin" séparée n'est utilisée que si un adminRefreshToken existe vraiment.
function sessionFor(url) {
  const useAdmin =
    SESSIONS.admin.match(url) && !!SESSIONS.admin.get().refreshToken;
  return useAdmin ? SESSIONS.admin : SESSIONS.user;
}

let pending = { user: null, admin: null };

function refreshTokens(name) {
  if (!pending[name]) {
    const session = SESSIONS[name];
    const used = session.get().refreshToken;

    pending[name] = (async () => {
      try {
        const { data } = await plain.post(session.refreshUrl, {
          refreshToken: used,
        });
        session.save(data);
        return data.token;
      } catch (err) {
        // Un autre onglet vient peut-être de renouveler : on réessaie avec son token
        const current = session.get().refreshToken;
        if (current && current !== used) {
          const { data } = await plain.post(session.refreshUrl, {
            refreshToken: current,
          });
          session.save(data);
          return data.token;
        }
        throw err;
      } finally {
        pending[name] = null;
      }
    })();
  }
  return pending[name];
}

function goToLogin(session) {
  const current = router.currentRoute.value;
  if (current.path !== session.loginRoute) {
    router.push({
      path: session.loginRoute,
      query: { redirect: current.fullPath },
    });
  }
}

// Déconnexion volontaire (bouton "Se déconnecter")
export async function logout(isAdmin = false) {
  const session = isAdmin ? SESSIONS.admin : SESSIONS.user;
  const { refreshToken } = session.get();

  try {
    if (refreshToken) {
      await plain.post(session.logoutUrl, { refreshToken });
    }
  } catch {
    // pas grave : on déconnecte quand même côté navigateur
  } finally {
    session.clear();
  }
}

// Ajoute le token courant aux requêtes vers l'API quand l'en-tête n'est pas déjà posé
axios.interceptors.request.use((config) => {
  const url = config.url || "";
  if (!url.startsWith(API_URL) || AUTH_URLS.test(url)) return config;

  const { token } = sessionFor(url).get();
  if (token && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;

    if (!response || response.status !== 401 || !config || config._retry) {
      return Promise.reject(error);
    }

    const url = config.url || "";
    if (AUTH_URLS.test(url)) return Promise.reject(error);

    const session = sessionFor(url);
    const name = session === SESSIONS.admin ? "admin" : "user";

    // Pas de refresh token : personne n'est connecté, on laisse la page gérer le 401
    if (!session.get().refreshToken) return Promise.reject(error);

    config._retry = true;

    try {
      const token = await refreshTokens(name);
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
      return axios(config);
    } catch (refreshError) {
      const status = refreshError.response?.status;

      // Le serveur a refusé le refresh : la session est vraiment terminée
      if (status === 400 || status === 401 || status === 403) {
        session.clear();
        goToLogin(session);
      }
      // Sinon (réseau coupé, serveur down) on garde la session
      return Promise.reject(error);
    }
  }
);
